import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiLinks } from '@cutting/app.api-links';
import { DadataService } from '@cutting/main/shared/services/dadata/dadata.service';
import {
  ChatMessage,
  ChatMessageResponse,
  CountertopRequest,
  CuttingButt,
  CuttingCadModel,
  CuttingCuttedMaterial,
  CuttingDocumentForOrder,
  CuttingFacadeMaterial,
  CuttingFileSettingsSyncDate,
  CuttingHoleDiameters,
  CuttingMaterial,
  CuttingMaterialFormat,
  CuttingOffcut,
  CuttingOrderDetail,
  CuttingOrderFurniture,
  CuttingOrderQuestion,
  CuttingPanel,
  CuttingPanelHelper,
  CuttingPointOfIssue,
  CuttingProduct,
  CuttingStaticDocument,
  CuttingTask,
  DeliveryTypeEnum,
  DownloadService,
  DraftBlock,
  Estimate,
  ExtendedAddress,
  Guid,
  ICustomResponse,
  IDadataAddressResponse,
  IDadataSuggestAddressRequest,
  LoadingState,
  MaterialForReplacementToElement,
  OrderModelsService,
  OrderStatusAction,
  PaginatedList,
  PanelListDataService,
  PlaceAutocompleteRequest,
  PlaceAutocompleteType,
  PlaceQueryAutocompleteResponseData,
  PriceList,
  PriceListProduct,
  PriceListProductBazis,
  PriceListProductGroup,
  SAVER,
  Saver,
  customResponse,
  getFileNameByContentDisposition,
} from 'cloud-shared-lib';
import { CadModelsDetailsInfoMaterial } from 'projects/cloud-shared-lib/src/lib/models/cutting/cutting-cad-model-details.model';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, concatMap, map, pluck, tap } from 'rxjs/operators';

interface DefaultMaterialSize {
  plate: CuttingMaterialFormat;
  linear: CuttingMaterialFormat;
}

export interface ICuttingOrderFurnitureResponse {
  furnituresDTO: CuttingOrderFurniture[];
  totalCount: number;
}

@Injectable()
export class OrderDetailService implements PanelListDataService, OrderModelsService {
  defaultOrderName = '';
  orderId: number | 'new';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  order: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onOrderChanged: BehaviorSubject<any> = new BehaviorSubject({});
  // onMaterialsChanged: BehaviorSubject<CuttingMaterial[]> = new BehaviorSubject([]);
  onCuttingMapsChanged: BehaviorSubject<string[]> = new BehaviorSubject([]);
  onEstimateChanged: BehaviorSubject<Estimate> = new BehaviorSubject(undefined);
  onEstimateOrderChanged: BehaviorSubject<Estimate> = new BehaviorSubject(undefined);
  onCutHistoryChanged: BehaviorSubject<CuttingTask[]> = new BehaviorSubject([]);
  // onCurrentMaterialChange: BehaviorSubject<any> = new BehaviorSubject({});
  onMessagesChanged = new BehaviorSubject<ChatMessageResponse>(null);
  onQuestionChanged = new BehaviorSubject<CuttingOrderQuestion>(null);
  onEndCutting = new BehaviorSubject<[number, number, string]>([0, 0, '']);
  defaultMaterialSize: DefaultMaterialSize;

  onCadModelsChanged: BehaviorSubject<CuttingCadModel[]> = new BehaviorSubject([]);
  onCadModelMaterialsParsed: Subject<CuttingCadModel> = new Subject();
  onCadModelMaterialsReplaced: BehaviorSubject<CuttingCadModel> = new BehaviorSubject(undefined);
  panelAttachmentsToUpload: File[] = [];

  newMessagesSubject: Subject<ChatMessage> = new Subject();

  constructor(
    private http: HttpClient,
    private dadataService: DadataService,
    private downloads: DownloadService,
    private route: ActivatedRoute,
    @Inject(SAVER) private saveAs: Saver
  ) {
    this.route.params.pipe(pluck('id')).subscribe(res => (this.orderId = res));
  }

  downloadPanelsCsv(
    orderId: number,
    materialInMatBaseId: number,
    panels: CuttingPanel[],
    schemaId: number
  ): Observable<Blob> {
    const httpParams = new HttpParams().set('schemaId', schemaId.toString());

    return this.downloads.blob(ApiLinks.panels2csv(materialInMatBaseId), {
      body: panels,
      filenameFromContentDisposition: true,
      defaultFilename: 'panels.csv',
      queryParams: httpParams,
    });
  }

  downloadPanelAttachment(orderId: number, materialInMatBaseId: number, attachmentName: string): Observable<Blob> {
    const notUploadedAttachment = this.panelAttachmentsToUpload.find(a => a.name === attachmentName);

    if (notUploadedAttachment) {
      return of(notUploadedAttachment as Blob).pipe(tap(f => this.saveAs(f, attachmentName)));
    }

    return this.downloads.blob(ApiLinks.api + 'order-attachments/download', {
      filenameFromContentDisposition: true,
      queryParams: new HttpParams({ fromObject: { orderId, materialInMatBaseId, attachmentName } }),
      defaultFilename: 'attachment',
    });
  }

  getOrder(): Observable<CuttingOrderDetail> {
    if (this.orderId === 'new') {
      this.order = undefined;

      const defaultOrderName$ = this.defaultOrderName ? of(this.defaultOrderName) : this.getDefaultOrderName();

      return defaultOrderName$.pipe(
        map(res => {
          this.defaultOrderName = res;
          this.onOrderChanged.next(false);
          return new CuttingOrderDetail();
        }),
        catchError(() => {
          this.onOrderChanged.next(false);
          return of(undefined);
        })
      );
    } else {
      return this.http.get<CuttingOrderDetail>(ApiLinks.order + '/' + this.orderId).pipe(
        map(res => {
          this.order = res;
          this.onOrderChanged.next(res);
          return res;
        })
      );
    }
  }

  saveOrder(order: CuttingOrderDetail): Promise<CuttingOrderDetail> {
    return new Promise((resolve, reject) => {
      this.http.put(ApiLinks.order + '/' + order.id, order).subscribe((response: CuttingOrderDetail) => {
        this.order = response;
        this.onOrderChanged.next(this.order);
        resolve(response);
      }, reject);
    });
  }

  changeOrderNote(orderId: number, newNote: string): Observable<void> {
    const httpParams = new HttpParams().set('newNote', newNote);

    return this.http.put<void>(`${ApiLinks.order}/${orderId}/note`, undefined, { params: httpParams });
  }

  changeOrderClientNote(orderId: number, newNote: string): Observable<void> {
    const httpParams = new HttpParams().set('newNote', newNote);

    return this.http.put<void>(`${ApiLinks.order}/${orderId}/clientNote`, undefined, { params: httpParams });
  }

  addOrder(order: CuttingOrderDetail): Promise<CuttingOrderDetail> {
    const contentHeaders: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json');

    return new Promise((resolve, reject) => {
      this.http.post(ApiLinks.order, order, { headers: contentHeaders }).subscribe((response: CuttingOrderDetail) => {
        resolve(response);
      }, reject);
    });
  }

  getDefaultOrderName(): Observable<string> {
    return this.http.get<string>(ApiLinks.defaultOrderName);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOrderFiles(): Observable<CuttingCadModel[]> {
    if (!isNaN(+this.orderId)) {
      return this.getCadModels(+this.orderId).pipe(tap(response => this.onCadModelsChanged.next(response)));
    } else {
      this.onCadModelsChanged.next([]);
      return of([]);
    }
  }

  getCuttingMaps(materialId?: number): Promise<string[]> {
    if (!isNaN(+this.orderId)) {
      const httpParams = new HttpParams().set('materialId', materialId ? materialId.toString() : '');

      return new Promise((resolve, reject) => {
        this.http
          .get(ApiLinks.cuttingMaps(+this.orderId), {
            params: httpParams,
          })
          .subscribe((response: string[]) => {
            this.onCuttingMapsChanged.next(response);
            resolve(response);
          }, reject);
      });
    } else {
      return new Promise(resolve => {
        this.onCuttingMapsChanged.next([]);
        resolve([]);
      });
    }
  }

  setCadModelDraftBlockMode(modelId: number, draftBlocks: DraftBlock[]): Observable<void> {
    return this.http.post<void>(ApiLinks.cadModel(modelId) + '/set-draft-blocks-mode', draftBlocks);
  }

  getMaterials(): Observable<CuttingMaterial[]> {
    if (!isNaN(+this.orderId)) {
      return this.http.get<CuttingMaterial[]>(ApiLinks.orderMaterial + '/' + +this.orderId);
    } else {
      return of([]);
    }
  }

  saveMaterials(materials: CuttingMaterial[], orderId: number): Observable<CuttingMaterial[]> {
    this.deleteEmptyPanels(materials);

    return this.http.post<CuttingMaterial[]>(ApiLinks.orderMaterial + '/' + orderId, materials);
  }

  saveFacadeMaterials(materials: CuttingFacadeMaterial[], orderId: number): Observable<CuttingFacadeMaterial[]> {
    return this.http.post<CuttingFacadeMaterial[]>(ApiLinks.orderFacadeMaterial(orderId), materials);
  }

  saveCountertops(countertops: CountertopRequest[], orderId: number): Observable<void> {
    return this.http.post<void>(ApiLinks.api + `countertops/order/${orderId}`, countertops);
  }

  saveOffcuts(materialId: number, items: CuttingOffcut[]): Promise<CuttingOffcut[]> {
    if (!isNaN(+this.orderId)) {
      const httpParams = new HttpParams().set('materialId', materialId.toString());

      return new Promise((resolve, reject) => {
        this.http
          .post(ApiLinks.offcuts(+this.orderId), items, {
            params: httpParams,
          })
          .subscribe(() => {
            resolve(undefined);
          }, reject);
      });
    } else {
      return new Promise(resolve => {
        resolve([]);
      });
    }
  }

  getOrderEstimate(): Promise<Estimate> {
    if (!isNaN(+this.orderId)) {
      return new Promise((resolve, reject) => {
        this.http.get(ApiLinks.estimate(+this.orderId)).subscribe((response: Estimate) => {
          this.onEstimateOrderChanged.next(response);
          resolve(response);
        }, reject);
      });
    } else {
      return new Promise(resolve => {
        this.onEstimateOrderChanged.next(new Estimate());
        resolve(new Estimate());
      });
    }
  }

  getVAT(): Observable<{ vatPercent: number; vatIncludeInPrice: boolean }> {
    let httpParams = new HttpParams();

    if (!isNaN(+this.orderId)) {
      httpParams = httpParams.set('orderId', this.orderId.toString());
    } else {
      httpParams = httpParams.set('orderId', '0');
    }

    return this.http.get<{ vatPercent: number; vatIncludeInPrice: boolean }>(ApiLinks.vat, { params: httpParams });
  }

  getCutHistory(): Observable<CuttingTask[]> {
    if (!isNaN(+this.orderId)) {
      return this.http
        .get<CuttingTask[]>(ApiLinks.cuttingHistory(+this.orderId))
        .pipe(tap(res => this.onCutHistoryChanged.next(res)));
    } else {
      this.onCutHistoryChanged.next([]);
      return of([]);
    }
  }

  cutHistoryCheckAll(): Promise<CuttingTask[]> {
    if (!isNaN(+this.orderId)) {
      return new Promise((resolve, reject) => {
        this.http
          .post(ApiLinks.cuttingHistory(+this.orderId) + '/check-all', undefined)
          .subscribe((response: CuttingTask[]) => {
            this.onCutHistoryChanged.next(response);
            resolve(response);
          }, reject);
      });
    } else {
      return new Promise(resolve => {
        this.onCutHistoryChanged.next([]);
        resolve([]);
      });
    }
  }

  setOrderStatus(statusAction: OrderStatusAction): Promise<CuttingOrderDetail> {
    if (!isNaN(+this.orderId)) {
      const httpParams = new HttpParams().set('statusAction', statusAction.toString());

      return new Promise((resolve, reject) => {
        this.http
          .post(ApiLinks.setOrderStatus(+this.orderId), undefined, {
            params: httpParams,
          })
          .subscribe((response: CuttingOrderDetail) => {
            this.order = response;
            this.onOrderChanged.next(this.order);
            resolve(response);
          }, reject);
      });
    } else {
      return new Promise(resolve => {
        resolve(new CuttingOrderDetail());
      });
    }
  }

  closeQuestion(questionId: number): Observable<void> {
    return this.http.post<void>(ApiLinks.closeQuestion(questionId), undefined).pipe(
      tap(() => {
        if (this.onQuestionChanged.value && this.onQuestionChanged.value.id === questionId) {
          this.onQuestionChanged.value.closed = true;
          this.onQuestionChanged.next(this.onQuestionChanged.value);
        }
      })
    );
  }

  restoreQuestion(questionId: number): Observable<void> {
    return this.http.post<void>(ApiLinks.restoreQuestion(questionId), undefined).pipe(
      tap(() => {
        if (this.onQuestionChanged.value && this.onQuestionChanged.value.id === questionId) {
          this.onQuestionChanged.value.closed = false;
          this.onQuestionChanged.next(this.onQuestionChanged.value);
        }
      })
    );
  }

  getCadModels(orderId: number): Observable<CuttingCadModel[]> {
    return this.http.get<CuttingCadModel[]>(ApiLinks.orderModel(orderId), {});
  }

  getCadModelsPromise(orderId: number): Promise<CuttingCadModel[]> {
    return new Promise((resolve, reject) => {
      this.getCadModels(orderId).subscribe((response: CuttingCadModel[]) => {
        this.onCadModelsChanged.next(response);
        resolve(response);
      }, reject);
    });
  }

  uploadCadModel(
    uploadedFiles: File[],
    orderId: number,
    readSettings?: { checkFurniture: boolean; checkAssembly: boolean }
  ): Observable<void> {
    const getFileGuid = (f: File): string =>
      (Guid.newGuid().replace(new RegExp('-', 'g'), '') + '.' + f.name.split('.').pop()).toLowerCase();

    const guided = uploadedFiles.slice(0, 10).map(f => ({ guid: getFileGuid(f), file: f }));

    guided.forEach(f => {
      const fakeCadModel = new CuttingCadModel();
      fakeCadModel.filename = f.file.name;
      fakeCadModel.guid = f.guid;
      fakeCadModel.count = 1;
      fakeCadModel.state = LoadingState.Uploading;

      this.onCadModelsChanged.value.unshift(fakeCadModel);

      this.onCadModelsChanged.next(this.onCadModelsChanged.value);
    });

    const form = new FormData();
    guided.forEach(g => form.append(g.guid, g.file, g.file.name));

    return this.http.post<void>(ApiLinks.orderModel(orderId), form, { params: readSettings });
  }

  deleteCadModel(idFile: number): Observable<void> {
    return this.http.delete<void>(ApiLinks.cadModel(idFile));
  }

  deleteCadModels(modelIds: number[]): Observable<void> {
    if (!isNaN(+this.orderId)) {
      let httpParams = new HttpParams().set('orderId', this.orderId.toString());
      modelIds.forEach(id => (httpParams = httpParams.append('modelIds', id)));

      return this.http.delete<void>(ApiLinks.deleteCadModels, { params: httpParams });
    } else return of(null);
  }

  uploadCadModelLinkMaterials(cadModel: CuttingCadModel, isRereadOriginal?: boolean): Observable<CuttingCadModel> {
    const httpParams: HttpParams = new HttpParams().set('isRereadOriginal', isRereadOriginal.toString());

    return this.http
      .post(ApiLinks.cadModelLinkMaterial(cadModel.id), cadModel.parsedMaterials, { params: httpParams })
      .pipe(
        catchError(() => of(undefined)),
        concatMap(() => of(cadModel))
      );
  }

  saveCadModel(cadModel: CuttingCadModel): Observable<void> {
    return this.http.put<void>(ApiLinks.cadModel(cadModel.id), cadModel);
  }

  getCuttedMaterials(): Observable<CuttingCuttedMaterial[]> {
    if (!isNaN(+this.orderId)) {
      return this.http.get<CuttingCuttedMaterial[]>(ApiLinks.cuttedMaterial(+this.orderId));
    } else {
      return of([]);
    }
  }

  findMaterialInMatbase(materials: string[]): Observable<{ [key: string]: CuttingMaterial }> {
    return this.http.post<{ [key: string]: CuttingMaterial }>(ApiLinks.findInMatbase, materials);
  }

  findButtMaterialInMatbase(materials: string[]): Observable<{ [key: string]: CuttingButt }> {
    return this.http.post<{ [key: string]: CuttingButt }>(ApiLinks.findButtInMatbase, materials);
  }

  getCadModelDetails(modelId: number): Observable<CadModelsDetailsInfoMaterial[]> {
    return this.http.get<CadModelsDetailsInfoMaterial[]>(ApiLinks.cadModelMaterials(modelId));
  }

  updateCadModelDetails(
    modelId: number,
    materials: CadModelsDetailsInfoMaterial[]
  ): Observable<CadModelsDetailsInfoMaterial[]> {
    return this.http.post<CadModelsDetailsInfoMaterial[]>(ApiLinks.cadModelMaterials(modelId), materials);
  }

  getAvailableTariffOptions(): Observable<ICustomResponse<string[]>> {
    return this.http.get(ApiLinks.availableTariffOptions).pipe(customResponse<string[]>());
  }

  getSystemDocuments(): Observable<ICustomResponse<CuttingDocumentForOrder[]>> {
    return this.http.get(ApiLinks.cuttingOrderSysDocuments).pipe(customResponse<CuttingDocumentForOrder[]>());
  }

  getCustomDocuments(): Observable<ICustomResponse<CuttingDocumentForOrder[]>> {
    return this.http.get(ApiLinks.cuttingOrderCustomDocuments).pipe(customResponse<CuttingDocumentForOrder[]>());
  }

  getStaticDocuments(orderId: number): Observable<ICustomResponse<CuttingStaticDocument[]>> {
    const httpParams: HttpParams = new HttpParams().set('orderId', orderId.toString());

    if (!isNaN(+this.orderId)) {
      return this.http
        .get(ApiLinks.cuttingStaticDocuments, { params: httpParams })
        .pipe(customResponse<CuttingStaticDocument[]>());
    } else {
      return of({ result: [], error: undefined });
    }
  }

  downloadCustomDocument(docId: number): Observable<Blob> {
    const httpParams: HttpParams = new HttpParams().set('docId', docId.toString());

    return this.http.get(ApiLinks.cuttingStaticDocuments + '/download', {
      params: httpParams,
      responseType: 'blob',
    });
  }

  getPriceListProductGroup(priceListId: number): Observable<ICustomResponse<PriceListProductGroup[]>> {
    return this.http.get(ApiLinks.elasticModelGroups(priceListId)).pipe(customResponse<PriceListProductGroup[]>());
  }

  getPriceLists(): Observable<ICustomResponse<PriceList[]>> {
    return this.http.get(ApiLinks.elasticModelPriceList).pipe(customResponse<PriceList[]>());
  }

  getPriceListProducts(
    priceListId: number,
    groupId: number,
    filter = '',
    pageIndex = 0,
    pageSize = 1000
  ): Observable<PaginatedList<PriceListProduct>> {
    const httpParams: HttpParams = new HttpParams()
      .set('filter', filter)
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    return this.http.get<PaginatedList<PriceListProduct>>(ApiLinks.elasticModelProducts(groupId, priceListId), {
      params: httpParams,
    });
  }

  getPriceListProductInfo(productId: number): Observable<ICustomResponse<PriceListProductBazis>> {
    return this.http.get(ApiLinks.elasticModelProductInfo(productId)).pipe(customResponse<PriceListProductBazis>());
  }

  getPiceListElementMaterials(
    productId: number,
    elementId: number
  ): Observable<ICustomResponse<MaterialForReplacementToElement[]>> {
    return this.http
      .get(ApiLinks.elasticModelElementMaterials(productId, elementId))
      .pipe(customResponse<MaterialForReplacementToElement[]>());
  }

  addCuttingProduct(selectedProductId: number, product: CuttingProduct): Observable<CuttingProduct> {
    const params = new HttpParams().set('productId', selectedProductId.toString());

    return this.http.post<CuttingProduct>(ApiLinks.elasticProduct(this.order.id), product, { params });
  }

  getElasticProducts(): Observable<ICustomResponse<CuttingProduct[]>> {
    if (this.order && this.order.id > 0) {
      return this.http.get(ApiLinks.elasticProduct(this.order.id)).pipe(customResponse<CuttingProduct[]>());
    } else {
      return of([]).pipe(customResponse<CuttingProduct[]>());
    }
  }

  deleteCuttingProduct(productId: number): Observable<ICustomResponse<CuttingProduct[]>> {
    return this.http
      .delete(ApiLinks.elasticProduct(this.order.id) + '/' + productId)
      .pipe(customResponse<CuttingProduct[]>());
  }

  deleteCuttingProducts(productIds: number[]): Observable<void> {
    if (!isNaN(+this.orderId)) {
      let httpParams = new HttpParams();
      productIds.forEach(id => (httpParams = httpParams.append('productIds', id)));

      return this.http.delete<void>(ApiLinks.elasticProduct(this.order.id) + '/delete-multiple', {
        params: httpParams,
      });
    } else return of(void 0);
  }

  getAvailableModelHolesDiams(): Observable<ICustomResponse<CuttingHoleDiameters>> {
    return this.http.get(ApiLinks.cadModelAvailableHoleDiams).pipe(customResponse<CuttingHoleDiameters>());
  }

  activatePromoCode(
    code: string
  ): Observable<{ materialDiscountPct: number; operationDiscountPct: number; orderDiscount: number }> {
    const params = new HttpParams().set('code', code);

    return this.http.post<{ materialDiscountPct: number; operationDiscountPct: number; orderDiscount: number }>(
      `${ApiLinks.order}/${this.order.id}/activate-promo-code`,
      undefined,
      { params }
    );
  }

  getOrderFurniture(): Observable<CuttingOrderFurniture[]> {
    if (!isNaN(+this.orderId)) {
      return this.http.get<CuttingOrderFurniture[]>(ApiLinks.getCuttingOrderFurniture(+this.orderId));
    } else {
      return of([]);
    }
  }

  addOrderFurnitures(furnitures: CuttingOrderFurniture[], orderId: number): Observable<void> {
    if (orderId) {
      return this.http.post<void>(ApiLinks.getCuttingOrderFurniture(orderId), furnitures);
    } else {
      return of(void 0);
    }
  }

  saveOrderFurniture(furniture: CuttingOrderFurniture): Observable<void> {
    if (!isNaN(+this.orderId)) {
      return this.http.put<void>(ApiLinks.cuttingOrderFurniture + '/' + furniture.id, furniture);
    } else {
      return of(void 0);
    }
  }

  deleteOrderFurniture(furnitureId: number): Observable<void> {
    if (!isNaN(+this.orderId)) {
      return this.http.delete<void>(ApiLinks.cuttingOrderFurniture + '/' + furnitureId);
    } else {
      return of(void 0);
    }
  }

  deleteOrderFurnitures(furnitureIds: number[]): Observable<void> {
    if (!isNaN(+this.orderId)) {
      let httpParams = new HttpParams();
      furnitureIds.forEach(id => (httpParams = httpParams.append('furnitureIds', id)));

      return this.http.delete<void>(ApiLinks.deleteMultipleFurnitures(+this.orderId), { params: httpParams });
    } else return of(void 0);
  }

  changeDeliveryType(deliveryType: DeliveryTypeEnum, orderId: number): Observable<void> {
    const paramsHttp: HttpParams = new HttpParams().set('deliveryType', deliveryType.toString());

    return this.http.post<void>(`${ApiLinks.order}/${orderId}/change-delivery-type`, undefined, {
      params: paramsHttp,
    });
  }

  changeDeliveryPoint(pointOfIssue: CuttingPointOfIssue, orderId: number): Observable<void> {
    return this.http.post<void>(`${ApiLinks.order}/${orderId}/change-delivery-point`, pointOfIssue);
  }

  changeDeliveryAddress(deliveryAddress: ExtendedAddress, orderId: number): Observable<void> {
    return this.http.post<void>(`${ApiLinks.order}/${orderId}/change-delivery`, deliveryAddress);
  }

  getPointsOfIssue(): Observable<ICustomResponse<CuttingPointOfIssue[]>> {
    return this.http.get(ApiLinks.pointsOfIssue).pipe(customResponse<CuttingPointOfIssue[]>());
  }

  getUserInfo(): Observable<ICustomResponse<{ publicApiKey: string }>> {
    return this.http.get(ApiLinks.userInfo).pipe(customResponse<{ publicApiKey: string }>());
  }

  cuttingProductChangeMaterials(product: CuttingProduct): Observable<void> {
    return this.http.post<void>(`${ApiLinks.elasticModels}/products/${product.id}/change-materials`, product);
  }

  saveCuttingProduct(product: CuttingProduct, productId: number): Observable<CuttingProduct> {
    return this.http.put<CuttingProduct>(
      `${ApiLinks.elasticModels}/product/order/${this.orderId}/${productId}`,
      product
    );
  }

  saveProductLinkMaterials(product: CuttingProduct): Observable<void> {
    return this.http.post<void>(
      `${ApiLinks.elasticModels}/products/${product.id}/set-link-material`,
      product.parsedMaterials
    );
  }

  getLogMaterials(logId: number): Observable<ICustomResponse<CuttingMaterial[]>> {
    return this.http
      .get(`${ApiLinks.order}/${this.orderId}/tasks/${logId}/materials`)
      .pipe(customResponse<CuttingMaterial[]>());
  }

  getMatchedButts(materialName: string, panelThickness = 0): Observable<ICustomResponse<CuttingButt[]>> {
    const httpParams: HttpParams = new HttpParams()
      .set('materialName', materialName)
      .set('panelThickness', panelThickness.toString());

    return this.http
      .get(ApiLinks.matchedButts + '/by-name', { params: httpParams })
      .pipe(customResponse<CuttingButt[]>());
  }

  addOrderToRemoteCart(linkForAddOrder: string): Observable<ICustomResponse<AddOrderToRemoteCartResponse>> {
    return this.http
      .post(linkForAddOrder, { orderId: this.order.id })
      .pipe(customResponse<AddOrderToRemoteCartResponse>());
  }

  downloadCadModel(modelId: number): Observable<{ body: Blob; filename: string }> {
    return this.http
      .get(ApiLinks.cadModel(modelId) + '/download', {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map(response => {
          const filename = getFileNameByContentDisposition(response.headers.get('Content-Disposition')) || 'model';

          return { body: response.body, filename: filename };
        })
      );
  }

  html2Pdf(html: string): Observable<ICustomResponse<Blob>> {
    return this.http.post(ApiLinks.html2Pdf, { value: html }, { responseType: 'blob' }).pipe(customResponse());
  }

  gatDadataAddressSuggestions(query: string, count = 5): Observable<IDadataAddressResponse[]> {
    return this.dadataService.gatAddress({
      query: query,
      count: count,
      locations: [
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          country_iso_code: 'RU',
        },
      ],
    } as IDadataSuggestAddressRequest);
  }

  gatGoogleAddressSuggestions(query: string, lang = 'en'): Observable<PlaceQueryAutocompleteResponseData> {
    const reqData = {
      input: query,
      language: lang,
      types: PlaceAutocompleteType.address,
    } as PlaceAutocompleteRequest;

    return this.http.post<PlaceQueryAutocompleteResponseData>(ApiLinks.googlePlaceAutocomplete, reqData);
  }

  getSettingsFilesSyncDate(): Observable<ICustomResponse<CuttingFileSettingsSyncDate>> {
    return this.http.get(ApiLinks.cuttingSettingFilesSyncDate).pipe(customResponse<CuttingFileSettingsSyncDate>());
  }

  markQuestionsAsRead(): Observable<void> {
    return this.http.put<void>(ApiLinks.cuttingOrderMessagesMarkAsRead(this.order.id), undefined);
  }

  addAdditionalOperation(additionalGUID: string, count: number): Observable<void> {
    const httpParams: HttpParams = new HttpParams().set('count', count);

    return this.http.post<void>(
      `${ApiLinks.order}/${this.order.id}/estimate/additional-operation/${additionalGUID}`,
      undefined,
      { params: httpParams }
    );
  }

  removeAdditionalOperation(additionalGUID: string): Observable<void> {
    return this.http.delete<void>(`${ApiLinks.order}/${this.order.id}/estimate/additional-operation/${additionalGUID}`);
  }

  removeAttachments(orderId: number, attachments: string[]): Observable<void> {
    const params = new HttpParams({ fromObject: { orderId, attachments } });
    return this.http.delete<void>(ApiLinks.api + 'order-attachments', { params });
  }

  private deleteEmptyPanels(materials: CuttingMaterial[]): void {
    materials.forEach(m => {
      m.panels = m.panels.filter(p => !CuttingPanelHelper.isEmptyPanel(p));
    });
  }
}

export interface AddOrderToRemoteCartResponse {
  cartLink: string;
  status: AddOrderToRemoteCartStatus;
  errorText: string;
}

export enum AddOrderToRemoteCartStatus {
  success,
  error,
}
